// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasShellContext } from "@insite/client-framework/Components/IsInShell";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { HiddenProps } from "@insite/mobius/Hidden";
import Page from "@insite/mobius/Page";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@turnerBlueprint/App/Components/Common/XngZone";
import ProductDetailsRelatedProductsAnchor from "@turnerBlueprint/App/Components/ProductDetails/ProductDetailsRelatedProductsAnchor";
import ProductDetailsSummary from "@turnerBlueprint/App/Components/ProductDetails/ProductDetailsSummary";
import { CustomAppState } from "@turnerBlueprint/App/Store/Reducers";
import { xIsAuthenticated } from "@turnerBlueprint/App/Store/Selectors/GeneralSelectors";
import { fontConfig } from "@turnerBlueprint/XDL/PreStyle/typography";
import { Divider } from "@turnerBlueprint/XDL/Utilities";
import * as React from "react";
import { css } from "styled-components";

export interface ProductDetailsViewStyles {
    container?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    leftColumnInnerContainer?: GridContainerProps;
    topGridItem?: GridItemProps;
    middleGridItem?: GridItemProps;
    middleGridItemHidden?: HiddenProps;
    bottomGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnGridItemHidden?: HiddenProps;
    // XNG-Change
    pageContainer?: InjectableCss;
    topSection?: InjectableCss;
    bottomSection?: InjectableCss;
    imageContainer?: InjectableCss;
    haveQuestionContainer?: InjectableCss;
    leftColumn?: InjectableCss;
    rightColumn?: InjectableCss;
    specificationsContainer?: InjectableCss;
    generalActionsContainer?: InjectableCss;
    priceActionsContainer?: InjectableCss;
    priceActionsRightSection?: InjectableCss;
    extraElementsContainer?: InjectableCss;
    infoDescriptionSection?: InjectableCss;
    actionsTagsSection?: InjectableCss;
    relatedProductsContainer?: InjectableCss;
    productNumbersWrapper?: InjectableCss;
    badgesContainer?: InjectableCss;
    stickyWrapper?: InjectableCss;
    stickyPage?: InjectableCss;
    priceAndActions?: InjectableCss;
    actionsWrapper?: InjectableCss;
    uomWrapper?: InjectableCss;
    addToCartWrapper?: InjectableCss;
    hiddenOnPrint?: InjectableCss;
}

export const viewStyles: ProductDetailsViewStyles = {
    leftColumnGridItem: {
        width: [12, 12, 8, 8, 8],
        css: css`
            overflow: hidden;
            direction: ltr;
        `,
    },
    topGridItem: { width: 12 },
    middleGridItem: { width: [12, 12, 0, 0, 0] },
    middleGridItemHidden: {
        above: "sm",
        css: css`
            width: 100%;
            direction: ltr;
        `,
    },
    bottomGridItem: { width: 12 },
    rightColumnGridItem: { width: [0, 0, 4, 4, 4] },
    rightColumnGridItemHidden: {
        below: "md",
        css: css`
            width: 100%;
            direction: ltr;
        `,
    },
    container: {
        css: css`
            direction: rtl;
        `,
    },
};

const styles = viewStyles;

// XNG-Change Add shell context

type Props = WidgetProps & HasShellContext & HasProductContext;

// XNG-Change: change structure to match design, Add sticky header
const ProductDetailsView: React.FC<Props> = props => {
    const [stickyVisible, setStickyVisible] = React.useState(false);
    const [hasRelatedProducts, setHasRelatedProducts] = React.useState(false);
    const relatedProductsRef = React.createRef<HTMLDivElement>();
    const addToCartRef = React.useRef<HTMLDivElement | null>(null);

    const isShellEditingMood = props.shellContext?.isEditing;
    const isAuthenticated = useAppSelector(state => xIsAuthenticated(state as CustomAppState));

    React.useEffect(() => {
        document.addEventListener("scroll", handleSticky, { passive: true });
        handleSticky();
        if (props.productContext.product.properties.hasRelatedProducts === "true") {
            setHasRelatedProducts(true);
        }
        return () => {
            document.removeEventListener("scroll", handleSticky);
            setHasRelatedProducts(false);
        };
    }, []);

    React.useEffect(() => {
        if (props.productContext.product.properties.hasRelatedProducts === "true") {
            setHasRelatedProducts(true);
        }
    }, [props.productContext.product.properties]);

    React.useEffect(() => {
        if (stickyVisible) {
            document.getElementById("header")?.classList?.add("hide");
        } else {
            document.getElementById("header")?.classList?.remove("hide");
        }
        return () => {
            document.getElementById("header")?.classList?.remove("hide");
        };
    }, [stickyVisible]);

    const handleSticky = () => {
        /* we need the height of the header to be excluded as we will overlap this area (add to cart button) */
        const partiallyPdpInfoHeight = (addToCartRef.current?.scrollHeight || 0) + 60;
        if (window.scrollY > partiallyPdpInfoHeight) {
            setStickyVisible(true);
        } else {
            setStickyVisible(false);
        }
    };

    return (
        <StyledWrapper {...styles.pageContainer}>
            <StyledWrapper data-test-selector="productDetails_topSection" {...styles.topSection}>
                <StyledWrapper {...styles.leftColumn} data-test-selector="productDetails_leftColumn">
                    <StyledWrapper className="hide-above-md">
                        <XngZone contentId={props.id} zoneName="Content5" hint="Title zone" />
                        <StyledWrapper {...styles.actionsTagsSection}>
                            <XngZone contentId={props.id} zoneName="Content6" hint="Actions & tags zone" />
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.imageContainer}>
                        <XngZone contentId={props.id} zoneName="Content0" hint="Image" />
                    </StyledWrapper>
                    <StyledWrapper {...styles.haveQuestionContainer} className="hide-sm">
                        <XngZone contentId={props.id} zoneName="Content00" hint="Have Question" />
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.rightColumn} data-test-selector="productDetails_rightColumn">
                    <StyledWrapper {...styles.infoDescriptionSection} ref={addToCartRef}>
                        <StyledWrapper className="hide-sm">
                            <XngZone contentId={props.id} zoneName="Content5" hint="Title zone" />
                            <StyledWrapper {...styles.actionsTagsSection}>
                                <XngZone contentId={props.id} zoneName="Content6" hint="Actions & tags zone" />
                            </StyledWrapper>
                        </StyledWrapper>
                        <Divider />
                        <StyledWrapper {...styles.productNumbersWrapper}>
                            {createWidgetElement("ProductDetails/ProductDetailsPartNumber", {
                                fields: {},
                            })}
                            {createWidgetElement("ProductDetails/ProductDetailsCustomerPartNumber", {
                                fields: {},
                            })}
                        </StyledWrapper>
                        <XngZone contentId={props.id} zoneName="Content7" hint="Description Zone" />
                        <Divider />
                        <StyledWrapper
                            {...styles.priceActionsContainer}
                            data-test-selector="productDetails_priceActions"
                        >
                            <StyledWrapper {...styles.priceAndActions}>
                                <XngZone contentId={props.id} zoneName="Content4" hint="Price" />
                                <StyledWrapper {...styles.actionsWrapper}>
                                    {createWidgetElement("ProductDetails/ProductDetailsQuantityOrdered", {
                                        fields: {},
                                    })}
                                    <StyledWrapper {...styles.uomWrapper} {...{ isAuthenticated }}>
                                        {createWidgetElement("ProductDetails/ProductDetailsUnitOfMeasureSelect", {
                                            fields: {},
                                        })}
                                    </StyledWrapper>

                                    <StyledWrapper {...styles.addToCartWrapper} {...{ isAuthenticated }}>
                                        {createWidgetElement("ProductDetails/ProductDetailsAddToCartButton", {
                                            fields: {},
                                        })}
                                    </StyledWrapper>

                                    {createWidgetElement("ProductDetails/ProductDetailsAddToListLink", {
                                        fields: {},
                                    })}
                                </StyledWrapper>
                            </StyledWrapper>

                            <Divider direction="vertical" margin="0 16px 0 22px" />
                            <StyledWrapper {...styles.extraElementsContainer}>
                                {hasRelatedProducts ? (
                                    <ProductDetailsRelatedProductsAnchor reference={relatedProductsRef} />
                                ) : (
                                    <>
                                        <Typography size={12} lineHeight="16px" weight={fontConfig.weight.bold}>
                                            {siteMessage("Custom_PDP_Extra_Information_Title")}
                                        </Typography>
                                        <Typography size={12} lineHeight="16px">
                                            {siteMessage("Custom_PDP_Extra_Information_Text")}
                                        </Typography>
                                    </>
                                )}
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.specificationsContainer} className="hide-md">
                        <XngZone contentId={props.id} zoneName="Content1" hint="Zone For Product Specifications" />
                        <Divider margin="0" />
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.specificationsContainer} className="hide-above-lg">
                <XngZone contentId={props.id} zoneName="Content1" hint="Zone For Product Specifications" />
                <Divider margin="0" />
            </StyledWrapper>
            <StyledWrapper {...styles.haveQuestionContainer} className="hide-above-md">
                <XngZone contentId={props.id} zoneName="Content00" hint="Have Question" />
            </StyledWrapper>
            <StyledWrapper {...styles.hiddenOnPrint}>
                <XngZone contentId={props.id} zoneName="Content13" hint="Zone For Content" />
            </StyledWrapper>
            <StyledWrapper data-test-selector="productDetails_bottomSection" {...styles.bottomSection}>
                <StyledWrapper {...styles.relatedProductsContainer} ref={relatedProductsRef}>
                    <XngZone contentId={props.id} zoneName="Content2" hint="Related Products" />
                </StyledWrapper>
                <XngZone contentId={props.id} zoneName="Content10" hint="Zone For Product carousel" />
            </StyledWrapper>
            <StyledWrapper
                className={`${stickyVisible ? "stickyVisible" : "stickyHidden"} ${
                    isShellEditingMood ? "editingMode" : ""
                }`}
                data-test-selector="productDetails-sticky-header"
                {...styles.stickyWrapper}
            >
                <Page background="common.white" {...styles.stickyPage}>
                    <ProductDetailsSummary />
                </Page>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProductContext(ProductDetailsView),
    definition: {
        displayName: "View",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
